<template>
    <!-- This example requires Tailwind CSS v2.0+ -->
    <div
        class="group my-4 w-full rounded-lg border border-iron-200 bg-white px-4 py-5 shadow-sm transition-all duration-200 hover:shadow-md"
    >
        <div class="items-center justify-between space-x-3 sm:flex">
            <div class="flex items-center gap-2">
                <div class="flex-shrink-0"></div>
                <div class="min-w-0 flex-1">
                    <p class="font-medium text-iron-900">
                        {{ workflow.name }}
                        <span
                            v-if="workflow.default"
                            class="inline-flex items-center rounded-md bg-iron-50 px-2 py-1 text-xs font-medium text-iron-600 ring-1 ring-inset ring-iron-500/10"
                            >{{ $t("workflows.default") }}</span
                        >
                    </p>
                    <p
                        v-if="workflow.trigger !== 'reservation_will_happen_in'"
                        class="text-sm text-iron-700"
                    >
                        {{
                            $t(
                                "workflows.trigger_send_to_" +
                                    workflow.target +
                                    "_when_" +
                                    workflow.trigger,
                            )
                        }}
                    </p>

                    <p v-else class="text-sm text-iron-700">
                        {{
                            $t(
                                "workflows.trigger_send_to_" +
                                    workflow.target +
                                    "_event_happen_in",
                            )
                        }}
                        {{
                            convertSeconds(
                                JSON.parse(workflow.data as any).timeBefore,
                            )
                        }}.
                    </p>
                </div>
            </div>

            <div
                class="mt-3 flex flex-shrink-0 justify-start self-center sm:mt-0"
            >
                <div class="inline-flex rounded-md shadow-sm">
                    <AssignEventTypeModal
                        :event-types="eventTypes"
                        :workflow-id="workflow.id"
                    >
                        <button
                            type="button"
                            class="relative inline-flex items-center gap-1 rounded-l-md bg-white px-3 py-2 text-sm text-iron-900 ring-1 ring-inset ring-iron-300 hover:bg-iron-50 focus:z-10"
                        >
                            <CalendarPlus :stroke-width="1.6" :size="20" />
                            <span class="hidden w-[50px] sm:inline-block">
                                {{ $t("workflows.assign") }}
                            </span>
                        </button>
                    </AssignEventTypeModal>

                    <button
                        :disabled="workflow.default"
                        class="relative -ml-px inline-flex items-center gap-1 rounded-r-md px-3 py-2 text-sm ring-1 ring-inset ring-iron-300 focus:z-10"
                        :class="{
                            'cursor-not-allowed bg-iron-100 text-iron-400':
                                workflow.default,
                            'bg-white text-iron-900 hover:bg-iron-50 ':
                                !workflow.default,
                        }"
                        @click="removeWorkflow"
                    >
                        <Trash2 :stroke-width="1.6" :size="20" />
                        <span class="hidden sm:inline-block">{{
                            $t("workflows.remove")
                        }}</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { onClickOutside } from "@vueuse/core";
import { router } from "@inertiajs/vue3";
import useConfirm from "../../ConfirmDialog/composables/useConfirm";

import { Trash2, CalendarPlus } from "lucide-vue-next";
import AssignEventTypeModal from "./AssignEventTypeModal.vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

export type Workflow = {
    id: number;
    team_id: number;
    name: string;
    default: boolean;
    trigger: string;
    target: "host" | "attendees";
    data: string | null;
    action: string;
    created_at: string;
    updated_at: string;
};

const props = defineProps<{
    workflow: Workflow;
    eventTypes: Array<any>;
}>();

const target = ref(null);
const opened = ref(false);

const { show } = useConfirm();

onClickOutside(target, () => (opened.value = false));

const removeWorkflow = async () => {
    if (props.workflow.default) {
        return;
    }

    const result = await show({
        header: t("workflows.remove_workflow_modal.are_you_sure"),
        message: t("workflows.remove_workflow_modal.delete"),
        confirm: t("remove"),
        cancel: t("cancel"),
    });

    if (result) {
        await router.delete(`workflows/${props.workflow.id}`);
    }
};

const convertSeconds = function (seconds: number) {
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    const result = [];

    if (days > 0) {
        result.push(`${days} ${t("workflows.days", days)}`);
    }

    const remainingHours = hours % 24;
    if (remainingHours > 0) {
        result.push(
            `${remainingHours} ${t("workflows.hours", remainingHours)}`,
        );
    }

    const remainingMinutes = minutes % 60;
    if (remainingMinutes > 0) {
        result.push(
            `${remainingMinutes} ${t("workflows.minutes", remainingMinutes)}`,
        );
    }

    const remainingSeconds = seconds % 60;
    if (remainingSeconds > 0) {
        result.push(
            `${remainingSeconds} ${t("workflows.seconds", remainingSeconds)}`,
        );
    }

    return result.join(", ");
};
</script>

<style>
@import url("/node_modules/@zaptime/vue3/dist/style.css");
</style>
